/**
 * KnowledgeBasePage.tsx — Knowledge Base (Conoscenza) ingest surface.
 * Per-tenant company docs that feed the AI Chat (KB mode). Documenti table +
 * domain filter + upload modal + AI-validator review queue (HITL) + decision
 * history. Reactive via kbStore. Ported from prototype stage-kb-ingest.jsx.
 * KB domain → wiki-llm-service (B1a, live); frontend wiring still mock.
 */
import { useAuth0 } from "@auth0/auth0-react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { I } from "../../components/icons";
import { ApiError, apiErrorMessage } from "../../lib/api";
import { config } from "../../lib/config";
import {
  useDecideReview,
  useDeleteKbDocument,
  useIsKbAdmin,
  useKbDocuments,
  useKbReviews,
  useUploadKbDocument,
} from "../../lib/kbApi";
import { kbStore } from "../../lib/kbStore";
import { MOCK_KB_DOMAINS, type KbDecision, type KbDoc, type KbDomain } from "../../lib/mock";
import { KBReviewModal } from "./KBReviewModal";
import { SynthesisProgress } from "./SynthesisProgress";
import {
  DECISION_PILL,
  DomainChip,
  DomainDot,
  DomainPill,
  KBPlaceholder,
  KBStatusPill,
  REC_LABEL,
  kbConfTone,
} from "./kbUi";

type Tab = "documenti" | "coda" | "storico";

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      style={{
        appearance: "none",
        border: "none",
        background: "transparent",
        padding: "12px 16px",
        fontSize: 12.5,
        fontWeight: 500,
        cursor: "pointer",
        color: active ? "var(--c-ink)" : "var(--c-ink-3)",
        borderBottom: active ? "2px solid var(--c-brand)" : "2px solid transparent",
        marginBottom: -1,
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      {children}
    </button>
  );
}

function fmtBytes(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1).replace(".", ",")} MB`;
  if (bytes >= 1000) return `${Math.round(bytes / 1000)} KB`;
  return `${bytes} B`;
}

// ─── Upload modal ────────────────────────────────────────────────────────────
function UploadModal({
  domains,
  onClose,
  onUpload,
  uploading,
}: {
  domains: KbDomain[];
  onClose: () => void;
  onUpload: (file: File, domain: string) => void;
  uploading: boolean;
}): JSX.Element {
  const [drag, setDrag] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [domain, setDomain] = useState("");
  const [domainOpen, setDomainOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const picked = domains.find((d) => d.key === domain);
  const ready = Boolean(file && domain) && !uploading;
  const openPicker = (): void => inputRef.current?.click();

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        background: "var(--c-bg-overlay)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        className="fade-in"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 560,
          maxWidth: "100%",
          background: "var(--c-bg-elev)",
          border: "1px solid var(--c-line)",
          borderRadius: 14,
          boxShadow: "var(--sh-pop)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "14px 18px",
            borderBottom: "1px solid var(--c-line)",
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 7,
              background: "var(--c-brand-soft)",
              color: "var(--c-brand)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <I.Upload size={15} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Carica nella Knowledge Base</div>
            <div style={{ fontSize: 11.5, color: "var(--c-ink-3)" }}>
              Archivio aziendale per-tenant · indicizzato per l&apos;AI Chat
            </div>
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>
            <I.X size={14} />
          </button>
        </div>

        <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
          <input
            ref={inputRef}
            type="file"
            style={{ display: "none" }}
            onChange={(e) => {
              const picked = e.target.files?.[0];
              if (picked) setFile(picked);
              e.target.value = "";
            }}
          />
          <div
            onDragEnter={(e) => {
              e.preventDefault();
              setDrag(true);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              const dropped = e.dataTransfer.files?.[0];
              if (dropped) setFile(dropped);
            }}
            style={{
              border: `1.5px dashed ${drag ? "var(--c-brand)" : "var(--c-line-strong)"}`,
              background: drag ? "var(--c-brand-soft)" : "var(--c-bg)",
              borderRadius: 12,
              padding: "28px 20px",
              textAlign: "center",
              transition: "all var(--tr-fast)",
            }}
          >
            {file ? (
              <div
                style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "center" }}
              >
                <I.PDF size={22} style={{ color: "#B53D2A" }} />
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{file.name}</div>
                  <div style={{ fontSize: 11.5, color: "var(--c-ink-3)" }}>
                    {fmtBytes(file.size)} · pronto
                  </div>
                </div>
                <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setFile(null)}>
                  <I.X size={13} />
                </button>
              </div>
            ) : (
              <>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 9,
                    background: "var(--c-brand-soft)",
                    color: "var(--c-brand)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 10,
                  }}
                >
                  <I.Upload size={18} />
                </div>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 3 }}>
                  Trascina un documento qui
                </div>
                <div style={{ fontSize: 12, color: "var(--c-ink-3)", marginBottom: 12 }}>
                  PDF · Excel · Markdown — fino a 50 MB
                </div>
                <button className="btn btn-sm" onClick={openPicker}>
                  Sfoglia file
                </button>
              </>
            )}
          </div>

          <div>
            <label
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: "var(--c-ink-2)",
                display: "block",
                marginBottom: 6,
              }}
            >
              Dominio <span style={{ color: "var(--c-err)" }}>*</span>
            </label>
            <div style={{ position: "relative" }}>
              <button
                className="btn"
                onClick={() => setDomainOpen((o) => !o)}
                style={{ width: "100%", justifyContent: "space-between", height: 34 }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    color: picked ? "var(--c-ink)" : "var(--c-ink-4)",
                  }}
                >
                  {picked && <DomainDot color={picked.color} />}
                  {picked ? picked.label : "Seleziona un dominio…"}
                </span>
                <I.ChevronD size={13} style={{ color: "var(--c-ink-3)" }} />
              </button>
              {domainOpen && (
                <>
                  <div
                    onClick={() => setDomainOpen(false)}
                    style={{ position: "fixed", inset: 0, zIndex: 1 }}
                  />
                  <div
                    className="fade-in"
                    style={{
                      position: "absolute",
                      top: "calc(100% + 4px)",
                      left: 0,
                      right: 0,
                      zIndex: 2,
                      background: "var(--c-bg-elev)",
                      border: "1px solid var(--c-line)",
                      borderRadius: 10,
                      boxShadow: "var(--sh-pop)",
                      padding: 6,
                    }}
                  >
                    {domains.map((d) => (
                      <button
                        key={d.key}
                        onClick={() => {
                          setDomain(d.key);
                          setDomainOpen(false);
                        }}
                        style={{
                          appearance: "none",
                          border: "none",
                          cursor: "pointer",
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: 9,
                          textAlign: "left",
                          padding: "8px 10px",
                          borderRadius: 7,
                          background: "transparent",
                          fontFamily: "inherit",
                          fontSize: 12.5,
                          fontWeight: 500,
                          color: "var(--c-ink)",
                        }}
                      >
                        <DomainDot color={d.color} />
                        <span style={{ flex: 1 }}>{d.label}</span>
                        {domain === d.key && (
                          <I.Check size={13} style={{ color: "var(--c-brand)" }} />
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 18px",
            borderTop: "1px solid var(--c-line)",
            background: "var(--c-bg)",
          }}
        >
          <span
            style={{
              fontSize: 11.5,
              color: "var(--c-ink-3)",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <I.Sparkle size={11} style={{ color: "var(--c-ai)" }} /> Il validatore AI può inviare il
            documento in revisione.
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-sm" onClick={onClose}>
              Annulla
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => file && onUpload(file, domain)}
              disabled={!ready}
              style={{ opacity: ready ? 1 : 0.5 }}
            >
              <I.Upload size={12} /> {uploading ? "Caricamento…" : "Carica"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Toast ─────────────────────────────────────────────────────────────────
function Toast({ message, onClose }: { message: string; onClose: () => void }): JSX.Element {
  return (
    <div
      className="fade-in"
      style={{
        position: "fixed",
        left: "50%",
        bottom: 24,
        transform: "translateX(-50%)",
        zIndex: 80,
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "11px 16px",
        borderRadius: 10,
        background: "#16191F",
        color: "#F2F1EE",
        boxShadow: "var(--sh-pop)",
        maxWidth: 560,
      }}
    >
      <I.CheckC size={15} style={{ color: "var(--c-ok)", flexShrink: 0 }} />
      <span style={{ fontSize: 12.5, lineHeight: 1.45 }}>{message}</span>
      <button
        onClick={onClose}
        style={{
          appearance: "none",
          border: "none",
          background: "transparent",
          color: "#9CA0AB",
          cursor: "pointer",
          padding: 0,
          marginLeft: 4,
          display: "flex",
        }}
      >
        <I.X size={13} />
      </button>
    </div>
  );
}

/** Error/consent state for the admin-only review tabs (shares the KB audience). */
function ReviewErrorState({
  error,
  consenting,
  onConsent,
}: {
  error: unknown;
  consenting: boolean;
  onConsent: () => void;
}): JSX.Element {
  const tokenError = error instanceof ApiError && error.status === 401;
  return (
    <div
      style={{
        padding: "40px 24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 12,
        textAlign: "center",
      }}
    >
      <I.Alert size={22} style={{ color: "var(--c-warn)" }} />
      <div style={{ fontSize: 13, color: "var(--c-ink-2)", maxWidth: 380 }}>
        {tokenError
          ? "Accesso alla Knowledge Base non ancora concesso."
          : `Impossibile caricare le revisioni: ${apiErrorMessage(error)}`}
      </div>
      {tokenError && (
        <button className="btn btn-primary btn-sm" onClick={onConsent} disabled={consenting}>
          {consenting ? "Apertura…" : "Concedi l'accesso alla Knowledge Base"}
        </button>
      )}
    </div>
  );
}

export function KnowledgeBasePage(): JSX.Element {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("documenti");
  const [domainFilter, setDomainFilter] = useState("Tutti");
  const [query, setQuery] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [reviewItem, setReviewItem] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Documenti reads LIVE from wiki-llm-service (B1a). Review queue + history are
  // admin-only (RLS + require_admin); gate the calls so non-admins don't 403-loop.
  const isAdmin = useIsKbAdmin();
  const docsQuery = useKbDocuments();
  const allDocs = docsQuery.data ?? [];
  const uploadDoc = useUploadKbDocument();
  const deleteDoc = useDeleteKbDocument();
  const pendingQuery = useKbReviews({ decided: false, enabled: isAdmin });
  const decidedQuery = useKbReviews({ decided: true, enabled: isAdmin });
  const decideReview = useDecideReview();
  const queue = pendingQuery.data ?? [];
  const decided = decidedQuery.data ?? [];

  // On localhost Auth0 forces a consent screen the silent token flow can't show
  // (consent_required), so the first KB token request fails. A one-time popup
  // consent records the grant; silent loads work afterward. Shown only on a 401
  // token error. (See Auth0 notes / B1b_backend_build_plan.)
  const { getAccessTokenWithPopup } = useAuth0();
  const [consenting, setConsenting] = useState(false);
  const tokenError = docsQuery.error instanceof ApiError && docsQuery.error.status === 401;
  async function grantKbAccess(): Promise<void> {
    setConsenting(true);
    try {
      await getAccessTokenWithPopup({
        authorizationParams: { audience: config.wikiLLMAudience },
      });
      await docsQuery.refetch();
      if (isAdmin) await Promise.all([pendingQuery.refetch(), decidedQuery.refetch()]);
    } catch {
      // popup blocked or dismissed — the error message stays visible
    } finally {
      setConsenting(false);
    }
  }

  function showToast(msg: string): void {
    setToast(msg);
  }

  function doUpload(file: File, domain: string): void {
    uploadDoc.mutate(
      { file, domain, title: file.name },
      {
        onSuccess: () => {
          setUploadOpen(false);
          showToast(`”${file.name}” caricato — in elaborazione…`);
        },
        onError: (err) => showToast(`Caricamento non riuscito: ${apiErrorMessage(err)}`),
      },
    );
  }

  function onDeleteDoc(d: KbDoc): void {
    if (!window.confirm(`Eliminare "${d.filename}" dalla Knowledge Base?`)) return;
    deleteDoc.mutate(d.id, {
      onSuccess: () => showToast(`"${d.filename}" eliminato.`),
      onError: (err) => showToast(`Eliminazione non riuscita: ${apiErrorMessage(err)}`),
    });
  }

  function applyDecision(id: string, decision: KbDecision, toastMsg: string): void {
    decideReview.mutate(
      { id, decision },
      {
        onSuccess: () => {
          setReviewItem(null);
          showToast(toastMsg);
        },
        onError: (err) => showToast(`Decisione non riuscita: ${apiErrorMessage(err)}`),
      },
    );
  }

  const filtered = allDocs.filter((d) => {
    if (d.status === "archived") return false; // soft-deleted docs are hidden from the active view
    if (domainFilter !== "Tutti" && d.domain !== domainFilter) return false;
    if (query.trim() && !d.filename.toLowerCase().includes(query.trim().toLowerCase()))
      return false;
    return true;
  });

  const reviewModalData = reviewItem
    ? [...queue, ...decided].find((r) => r.id === reviewItem)
    : null;

  return (
    <div style={{ padding: "20px 28px 60px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <div>
          <div className="t-eyebrow" style={{ marginBottom: 6 }}>
            Archivio · per-tenant
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>
            Knowledge Base
          </h1>
          <p style={{ fontSize: 13, color: "var(--c-ink-3)", marginTop: 4 }}>
            Conoscenza aziendale · archivio per-tenant che alimenta l&apos;AI Chat (modalità KB).
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setUploadOpen(true)}>
          <I.Upload size={13} /> Carica documenti
        </button>
      </div>

      <div className="card" style={{ overflow: "hidden" }}>
        <div style={{ display: "flex", borderBottom: "1px solid var(--c-line)" }}>
          <TabBtn active={tab === "documenti"} onClick={() => setTab("documenti")}>
            Documenti
          </TabBtn>
          <TabBtn active={tab === "coda"} onClick={() => setTab("coda")}>
            Coda revisione
            {queue.length > 0 && (
              <span
                className="pill pill-warn"
                style={{ marginLeft: 8, height: 16, padding: "0 6px", fontSize: 10, gap: 3 }}
              >
                <I.Alert size={9} /> {queue.length}
              </span>
            )}
          </TabBtn>
          <TabBtn active={tab === "storico"} onClick={() => setTab("storico")}>
            Storico
          </TabBtn>
        </div>

        {tab === "documenti" && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 16px",
                borderBottom: "1px solid var(--c-line)",
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <DomainChip
                  label="Tutti"
                  active={domainFilter === "Tutti"}
                  onClick={() => setDomainFilter("Tutti")}
                />
                {MOCK_KB_DOMAINS.map((d) => (
                  <DomainChip
                    key={d.key}
                    label={d.label}
                    color={d.color}
                    active={domainFilter === d.key}
                    onClick={() => setDomainFilter(d.key)}
                  />
                ))}
              </div>
              <div style={{ flex: 1 }} />
              <div style={{ position: "relative" }}>
                <I.Search
                  size={13}
                  style={{
                    position: "absolute",
                    left: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--c-ink-4)",
                  }}
                />
                <input
                  className="input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Cerca documenti…"
                  style={{ height: 28, fontSize: 12, paddingLeft: 26, width: 220 }}
                />
              </div>
            </div>

            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 64 }}>ID</th>
                  <th>Documento</th>
                  <th style={{ width: 140 }}>Dominio</th>
                  <th style={{ width: 140 }}>Caricato da</th>
                  <th style={{ width: 110 }}>Data</th>
                  <th style={{ width: 80, textAlign: "right" }}>Chunk</th>
                  <th style={{ width: 130 }}>Stato</th>
                </tr>
              </thead>
              <tbody>
                {docsQuery.isPending && (
                  <tr>
                    <td
                      colSpan={7}
                      style={{ textAlign: "center", color: "var(--c-ink-3)", padding: "32px 0" }}
                    >
                      Caricamento documenti…
                    </td>
                  </tr>
                )}
                {docsQuery.isError && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", padding: "28px 16px" }}>
                      <div
                        style={{
                          color: "var(--c-err)",
                          fontSize: 12.5,
                          marginBottom: tokenError ? 12 : 0,
                        }}
                      >
                        {apiErrorMessage(docsQuery.error)}
                      </div>
                      {tokenError && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => void grantKbAccess()}
                          disabled={consenting}
                        >
                          {consenting ? "Apertura…" : "Concedi l'accesso alla Knowledge Base"}
                        </button>
                      )}
                    </td>
                  </tr>
                )}
                {!docsQuery.isPending &&
                  !docsQuery.isError &&
                  filtered.map((d: KbDoc) => {
                    const dom = kbStore.domain(d.domain);
                    const genPages = d.generatedPageIds ?? [];
                    return (
                      <tr key={d.id}>
                        <td className="t-mono" style={{ color: "var(--c-ink-3)", fontSize: 12 }}>
                          {d.id}
                        </td>
                        <td>
                          <div
                            style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}
                          >
                            <DomainDot color={dom ? dom.color : "var(--c-ink-4)"} />
                            <span
                              style={{
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {d.filename}
                            </span>
                          </div>
                          {genPages.length > 0 && (
                            <button
                              onClick={() => navigate("/kb/explore")}
                              style={{
                                appearance: "none",
                                border: "none",
                                background: "transparent",
                                cursor: "pointer",
                                fontFamily: "inherit",
                                display: "inline-block",
                                padding: 0,
                                marginTop: 2,
                                marginLeft: 16,
                                fontSize: 11,
                                fontWeight: 500,
                                color: "var(--c-info)",
                              }}
                            >
                              → {genPages.length}{" "}
                              {genPages.length === 1 ? "pagina generata" : "pagine generate"}
                            </button>
                          )}
                        </td>
                        <td>
                          <DomainPill domainKey={d.domain} />
                        </td>
                        <td style={{ color: "var(--c-ink-2)", fontSize: 12.5 }}>{d.uploader}</td>
                        <td style={{ color: "var(--c-ink-3)", fontSize: 12 }}>{d.date}</td>
                        <td
                          className="t-num t-mono"
                          style={{ textAlign: "right", color: "var(--c-ink-2)" }}
                        >
                          {d.chunks}
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 8,
                            }}
                          >
                            {d.status === "ingesting" ? (
                              <SynthesisProgress variant="compact" stage={d.synthesisStage} />
                            ) : (
                              <KBStatusPill status={d.status} />
                            )}
                            <button
                              className="btn btn-ghost btn-icon btn-sm"
                              title="Elimina documento"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteDoc(d);
                              }}
                              disabled={deleteDoc.isPending}
                            >
                              <I.Trash size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                {!docsQuery.isPending && !docsQuery.isError && filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      style={{ textAlign: "center", color: "var(--c-ink-3)", padding: "32px 0" }}
                    >
                      Nessun documento corrisponde ai filtri.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div
              style={{
                padding: "10px 16px",
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "var(--c-ink-3)",
                fontSize: 12,
              }}
            >
              <I.Info size={12} /> {filtered.length} document{filtered.length === 1 ? "o" : "i"} ·
              archivio scoped al tenant attivo · nessun dato condiviso tra organizzazioni.
            </div>
          </>
        )}

        {tab === "coda" &&
          (!isAdmin ? (
            <KBPlaceholder
              icon={I.Alert}
              title="Riservato agli amministratori"
              body="La coda di revisione del validatore AI è visibile solo agli amministratori del workspace."
            />
          ) : pendingQuery.isPending ? (
            <KBPlaceholder
              icon={I.Sparkle}
              title="Caricamento…"
              body="Recupero della coda di revisione dal validatore AI."
            />
          ) : pendingQuery.isError ? (
            <ReviewErrorState
              error={pendingQuery.error}
              consenting={consenting}
              onConsent={() => void grantKbAccess()}
            />
          ) : queue.length === 0 ? (
            <KBPlaceholder
              icon={I.CheckC}
              title="Nessuna revisione in sospeso"
              body="Tutti i documenti segnalati dal validatore AI sono stati gestiti. Le decisioni passate sono nella scheda Storico."
            />
          ) : (
            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 12px",
                  borderRadius: 8,
                  background: "var(--c-ai-soft)",
                  fontSize: 12,
                  color: "var(--c-ai)",
                }}
              >
                <I.Sparkle size={13} />
                <span>
                  Il validatore AI ha segnalato <strong>{queue.length}</strong> possibili
                  contraddizioni. È richiesta una decisione umana (Human-in-the-Loop).
                </span>
              </div>
              {queue.map((r) => {
                const dom = kbStore.domain(r.domain);
                return (
                  <div className="card" key={r.id} style={{ overflow: "hidden" }}>
                    <div
                      style={{
                        padding: "12px 16px",
                        borderBottom: "1px solid var(--c-line)",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <I.Alert size={15} style={{ color: "var(--c-warn)", flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <span style={{ fontSize: 13, fontWeight: 600 }}>
                            Possibile contraddizione
                          </span>
                          <DomainPill domainKey={r.domain} />
                          <span
                            style={{
                              fontSize: 12,
                              color: kbConfTone(r.confidence),
                              fontWeight: 600,
                            }}
                          >
                            {Math.round(r.confidence * 100)}% confidenza
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: "var(--c-ink-2)", marginTop: 3 }}>
                          <span className="t-mono" style={{ color: "var(--c-ink-3)" }}>
                            {r.newDocId}
                          </span>{" "}
                          · {r.newDocSource}
                        </div>
                        <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", marginTop: 1 }}>
                          Caricato da {r.uploader} · {r.uploadedAt}
                        </div>
                      </div>
                      {dom && (
                        <span className="t-mono pill" style={{ flexShrink: 0 }}>
                          {r.id}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        padding: "12px 16px",
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        flexWrap: "wrap",
                      }}
                    >
                      <span className="ai-badge">
                        <I.Sparkle size={9} />
                        AI
                      </span>
                      <span
                        style={{ fontSize: 12.5, color: "var(--c-ink-2)", flex: 1, minWidth: 200 }}
                      >
                        Suggerimento:{" "}
                        <strong style={{ color: "var(--c-ink)" }}>
                          {REC_LABEL[r.aiRecommendation]}
                        </strong>
                      </span>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => setReviewItem(r.id)}
                      >
                        <I.Eye size={13} /> Apri revisione completa
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

        {tab === "storico" &&
          (!isAdmin ? (
            <KBPlaceholder
              icon={I.Alert}
              title="Riservato agli amministratori"
              body="Lo storico delle decisioni di revisione è visibile solo agli amministratori del workspace."
            />
          ) : decidedQuery.isPending ? (
            <KBPlaceholder
              icon={I.Sparkle}
              title="Caricamento…"
              body="Recupero dello storico delle decisioni."
            />
          ) : decidedQuery.isError ? (
            <ReviewErrorState
              error={decidedQuery.error}
              consenting={consenting}
              onConsent={() => void grantKbAccess()}
            />
          ) : decided.length === 0 ? (
            <KBPlaceholder
              icon={I.History}
              title="Nessuna decisione registrata"
              body="Le decisioni di revisione approvate dall'Admin compariranno qui."
            />
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 64 }}>ID</th>
                  <th>Documento</th>
                  <th style={{ width: 130 }}>Dominio</th>
                  <th style={{ width: 170 }}>Decisione</th>
                  <th style={{ width: 150 }}>Decisor</th>
                  <th style={{ width: 150 }}>Data decisione</th>
                </tr>
              </thead>
              <tbody>
                {decided.map((r) => {
                  const dp = r.decision ? DECISION_PILL[r.decision] : DECISION_PILL.reject_new;
                  return (
                    <tr key={r.id}>
                      <td className="t-mono" style={{ color: "var(--c-ink-3)", fontSize: 12 }}>
                        {r.id}
                      </td>
                      <td style={{ fontWeight: 500, fontSize: 12.5 }}>{r.newDocSource}</td>
                      <td>
                        <DomainPill domainKey={r.domain} />
                      </td>
                      <td>
                        {dp.muted ? (
                          <span className="pill" style={{ opacity: 0.6 }}>
                            {dp.label}
                          </span>
                        ) : (
                          <span className={`pill ${dp.cls}`}>{dp.label}</span>
                        )}
                      </td>
                      <td style={{ fontSize: 12.5, color: "var(--c-ink-2)" }}>{r.decidedBy}</td>
                      <td style={{ fontSize: 12, color: "var(--c-ink-3)" }}>{r.decidedAt}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ))}
      </div>

      {uploadOpen && (
        <UploadModal
          domains={MOCK_KB_DOMAINS}
          onClose={() => setUploadOpen(false)}
          onUpload={doUpload}
          uploading={uploadDoc.isPending}
        />
      )}
      {reviewModalData && (
        <KBReviewModal
          review={reviewModalData}
          onClose={() => setReviewItem(null)}
          onDecide={applyDecision}
        />
      )}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
}
